import {BookingConfig, BookingPricing, BookingSpaceConfig} from "@features/leasing/lease-bookings/bookings.model";
import {PanelType} from "@shared-components/info-panel/info-panel.component";
import {Auditable} from "@ui-kit/framework/security/model/auditable.model";
import {OfferingSpacesInterface} from "../spaces/model/offering-spaces.interface";
import {SpaceDefaultRate, TimePeriod} from "../spaces/model/space-default-pricing.interface";

export class LeaseSummary {
  uuid!: string;
  parentUuid!: string | null;
  retailerId!: number;
  districtUuid?: string;
  occupancyType?: OccupancyType;
  agreementType?: AgreementType;
  leaseType?: string;
  leaseStatus!: LeaseStatus;
  leaseStructureType!: string;
  districtName?: string;
  districtKey?: string;
  districtDescription?: string;
  spaceNames?: string;
  retailerCompanyName?: string;
  retailerWebsiteUrl?: string;
  retailerDescription?: string;
  leaseEndDate!: string;
  leaseStartDate!: string;
  totalSize!: number;
  termMonths?: number;
  monthlyPricePerSqm?: number | null;
  turnoverPercentageValue?: number | null;
  rentalType?: RentalPricingOption | null;
  leaseLocation!: string | null;
  retailerIndustryTypes!: string | null;
  expiresInDays!: number | null;
  totalMonthlyFee!: number | null;
  bankAccountRef!: string | null;
  leaseGridSpaces?: LeaseGridSpace[];
  tradingOutsideOfLeasePeriod!: boolean;
  baseBookingConfig?: BookingConfig;

  constructor(obj: any) {
    Object.assign(this, obj);
  }
}

export enum LeaseStatus {
  PLANNED = 'PLANNED',
  UNSIGNED = 'UNSIGNED',
  INVALID = 'INVALID',
  CANCELED = 'CANCELED',
  APPROVED = 'APPROVED',
  UPCOMING = 'UPCOMING',
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  TERMINATED = 'TERMINATED'
}

export class Period {
  startDate?: any | null = null;
  endDate?: any | null = null;
  latestTransactionDate?: any | null = null;
  earliestTransactionDate?: any | null = null;

  constructor(init?: Partial<Period>) {
    if (init) {
      Object.assign(this, init);
    }
    if (this.startDate) {
      this.startDate = new Date(this.startDate * 1000); // Convert to milliseconds
    }
    if (this.endDate) {
      this.endDate = new Date(this.endDate * 1000); // Convert to milliseconds
    }
    if (this.latestTransactionDate) {
      this.latestTransactionDate = new Date(this.latestTransactionDate * 1000); // Convert to milliseconds
    }
    if (this.earliestTransactionDate) {
      this.earliestTransactionDate = new Date(this.earliestTransactionDate * 1000); // Convert to milliseconds
    }
  }
}

export class PaymentConfigData extends Period {
  uuid: string | null = null;
  leaseUuid?: string | null = null;
  districtUuid?: string | null;
  activationStatus: string | null = null;
  billingUpdateRequested: string | null = null;
  transactionSource: string | null = null;
  transactionSourceConfig: TransactionSourceConfig | SupplierSourceConfig | null = null;
  active: boolean | null = null;
  minStartDate: any | null = null;
  maxEndDate: any | null = null;
}

export class PaymentConfigRequest extends Period {
  uuid: string | null = null;
  leaseUuid?: string | null = null;
  districtUuid?: string | null = null;
  activationStatus: string | null = null;
  billingUpdateRequested: string | null = null;
  transactionSource: string | null = null;
  transactionSourceConfig: string | null = null;
  active: boolean | null = null;
  minStartDate: any | null = null;
  maxEndDate: any | null = null;
  occupancyType?: OccupancyType | null = null;
  agreementType?: AgreementType | null = null;
  retailerCompanyName?: string | null = null;
}

export class LeaseSupplierState {
  validAssignedSuppliers!: { [key: string]: LeaseSummary };
  invalidAssignedSuppliers!: { [key: string]: LeaseSummary };
  unassignedSuppliers: ProductSupplier[] = [];

  constructor(obj: any) {
    Object.assign(this, obj);
  }

  get validAssignedSuppliersMap(): Map<ProductSupplier, LeaseSummary> {
    const map = new Map<ProductSupplier, LeaseSummary>();
    Object.keys(this.validAssignedSuppliers).forEach(key => {
      // Assuming ProductSupplier and LeaseSummary are defined properly
      const supplier: ProductSupplier = deserializeProductSupplier(key);
      const summary: LeaseSummary = this.validAssignedSuppliers[key];
      map.set(supplier, summary);
    });
    return map;
  }

  get invalidAssignedSuppliersMap(): Map<ProductSupplier, LeaseSummary> {
    const map = new Map<ProductSupplier, LeaseSummary>();
    Object.keys(this.invalidAssignedSuppliers).forEach(key => {
      // Assuming ProductSupplier and LeaseSummary are defined properly
      const supplier: ProductSupplier = deserializeProductSupplier(key);
      const summary: LeaseSummary = this.invalidAssignedSuppliers[key];
      map.set(supplier, summary);
    });
    return map;
  }
}

function deserializeProductSupplier(key: string): ProductSupplier {
  const uuid = key.match(/uuid=(.*?),/)![1];
  const name = key.match(/name=(.*?),/)![1];
  const description = key.match(/description=(.*?),/)![1];
  const posAccountUuid = key.match(/posAccountUuid=(.*?),/) ? key.match(/posAccountUuid=(.*?),/)![1] : null;
  return {uuid, name, description, posAccountUuid};
}

export interface LeaseSupplierSummary {
  leaseUuid: string;
  retailerCompanyName: string;
  supplierUuid: string;
  supplierName: string
}

export function mapRequestsToData(requests: PaymentConfigRequest[]): PaymentConfigData[] {
  return requests.map((request: PaymentConfigRequest) => {
    const {
      uuid,
      leaseUuid,
      activationStatus,
      billingUpdateRequested,
      transactionSource,
      transactionSourceConfig,
      active,
      startDate,
      endDate,
      earliestTransactionDate,
      latestTransactionDate,
      minStartDate,
      maxEndDate,
    } = request;
    return {
      uuid,
      leaseUuid,
      activationStatus,
      billingUpdateRequested,
      transactionSource,
      transactionSourceConfig: JSON.parse(transactionSourceConfig as string),
      active,
      startDate,
      endDate,
      earliestTransactionDate,
      latestTransactionDate,
      minStartDate,
      maxEndDate
    };
  })
}

export function mapRequestToData(request: PaymentConfigRequest): PaymentConfigData {
  const {
    uuid,
    leaseUuid,
    districtUuid,
    activationStatus,
    billingUpdateRequested,
    transactionSource,
    transactionSourceConfig,
    active,
    startDate,
    endDate,
    earliestTransactionDate,
    latestTransactionDate,
    minStartDate,
    maxEndDate,
  } = request;
  return {
    uuid,
    leaseUuid,
    districtUuid,
    activationStatus,
    billingUpdateRequested,
    transactionSource,
    transactionSourceConfig: JSON.parse(transactionSourceConfig as string),
    active,
    startDate,
    endDate,
    earliestTransactionDate,
    latestTransactionDate,
    minStartDate,
    maxEndDate,
  };
}

/**
 * The write-side counterpart of `mapRequestToData`: turns a transaction source config into the
 * JSON string the API stores.
 *
 * For VEND/POSTER this is a *denylist* — it strips the keys the API keeps in dedicated columns
 * and stringifies whatever is left, so new config fields (e.g. CS-1369's `billingDayOfMonth`)
 * round-trip without being listed here. Extracted from LeasePosSettingsComponent so the round
 * trip is directly unit testable and so lease-details can persist the same shape.
 */
export function buildTransactionSourceConfigString(transactionSource: string | null, requestConfig: {}): string {
  if (transactionSource == TransactionSource.NETCASH) {
    return JSON.stringify({
      transactionReferences: (requestConfig as TransactionSourceConfig).transactionReferences,
      paymentSourceName: (requestConfig as TransactionSourceConfig).paymentSourceName,
      paymentTypeName: (requestConfig as TransactionSourceConfig).paymentTypeName
    });
  }
  //   remove all common keys not required in the string
  if ('startDate' in requestConfig) delete (requestConfig as any)['startDate'];
  if ('endDate' in requestConfig) delete (requestConfig as any)['endDate'];
  if ('earliestTransactionDate' in requestConfig) delete (requestConfig as any)['earliestTransactionDate'];
  if ('latestTransactionDate' in requestConfig) delete (requestConfig as any)['latestTransactionDate'];
  if ('minStartDate' in requestConfig) delete (requestConfig as any)['minStartDate'];
  if ('maxEndDate' in requestConfig) delete (requestConfig as any)['maxEndDate'];
  if ('activationStatus' in requestConfig) delete (requestConfig as any)['activationStatus'];
  if ('transactionReferences' in requestConfig) delete (requestConfig as any)['transactionReferences']

  //   remove extra for POSTER and VEND
  if (transactionSource == TransactionSource.POSTER || transactionSource == TransactionSource.VEND) {
    if ('paymentConfigUuid' in requestConfig)
      delete (requestConfig as any)['paymentConfigUuid'];
    if ('displayName' in requestConfig)
      delete (requestConfig as any)['displayName'];
  }

  return JSON.stringify(requestConfig);
}

export class LeasePricingPeriod extends Period {
  uuid: string | null = null;
  leaseUuid: string | null = null;
  leasePaymentConfigUuid: string | null = null;
  applyTransactionFee: boolean | null = null;
  transactionFeeType: string | null = null;
  transactionFee: number | null = null;
  rentalPricingOption: RentalPricingOption | null = null;
  rentalPricingTurnoverPercentageValue: number | null = null;
  fixedRentalPricingValue: number | null = null;
  isEditable: boolean = true;
  bookingPricing: BookingPricing | null = null;
}

export enum RentalPricingOption {
  FIXED_PER_SQM_PER_MONTH = 'FIXED_PER_SQM_PER_MONTH',
  FIXED_PER_MONTH = 'FIXED_PER_MONTH',
  FIXED_FOR_PERIOD = 'FIXED_FOR_PERIOD',
  PRICING_MODEL = 'PRICING_MODEL',
  TURNOVER_PERCENTAGE = 'TURNOVER_PERCENTAGE',
  FIXED_COMPLEX = 'FIXED_COMPLEX'
}

export class TransactionSourceConfig extends Period {
  domainPrefix: string | null = null;
  displayName?: string | null = null;
  outletId: string | null = null;
  paymentTypeName: string | null = null;
  accessToken: string | null = null;
  paymentSourceName: string | null = null;
  loginUrl: string | null = null;
  loginUsername: string | null = null;
  loginPassword: string | null = null;
  transactionReferences: string[] = [];
  /**
   * CS-1369 — Lightspeed's "plan renewal" day of the month (1..31). Not exposed by the X-series
   * API, so it is captured manually and may be absent until an internal admin fills it in.
   */
  billingDayOfMonth: number | null = null;
  paymentConfigUuid?: string | null = null;
  activationStatus?: string | null;
  minStartDate?: any | null = null;
  maxEndDate?: any | null = null;
}

export class SupplierSourceConfig extends Period {
  paymentConfigUuid: string | null = null;
  suppliers: ProductSupplier[] | null = null;
  minStartDate?: any | null = null;
  maxEndDate?: any | null = null;
}

export class SupplierConfigSummary extends Period {
  uuid: string | null = null;
  domainPrefix: string | null = null;
  supplierName: string | null = null;
}

export class TransactionSourceRequest {
  domainPrefix: string | null = null;
  outletId: string | null = null;
  paymentTypeName: string | null = null;
  accessToken: string | null = null;
  paymentSourceName: string | null = null;
  loginUrl: string | null = null;
  loginUsername: string | null = null;
  loginPassword: string | null = null;
}

export class LeaseGridSpace {
  uuid!: string;
  leaseUuid!: string | null;
  occupancyType!: OccupancyType;
  agreementType!: AgreementType;
  districtSpaceId!: number;
  leaseStartDate!: string;
  leaseEndDate!: string;
  availabilityStatus!: string | null;
  spaceSize!: number | null;
  spaceName!: string | null;
  retailerId!: number | null;
  retailerCompanyName!: string | null;
  leaseStatus!: LeaseStatus | null;
  periodGroupUuid!: string | null;
  periodGroupRanking!: number | null;
  bookingSpaceConfig: BookingSpaceConfig | null = null;
}

export class LeaseDocument extends Auditable {
  documentUuid!: string;
  documentTypeCode!: string;
  leaseUuid!: string;
  fileName!: string;
  mimeType!: string;
  fileUuid!: string;
  versionNo!: number;

  constructor(obj: any) {
    super(obj);
    Object.assign(this, obj);
  }
}

export class LeaseDocumentType {
  code!: string;
  name!: string;
  description!: string;
  active!: boolean;
}

export class PaymentMethod {
  uuid!: string;
  districtUuid!: string;
  transactionSource!: string;
  name!: string;
  collectionType!: string;
  transactionFeeType!: string;
  transactionFee!: number;
}


export class CardReader {
  cardReaderCode: string | null = null;
  uuid: string | null = null;
  leaseUuid: string | null = null;
  startDate: string | null = null;
  endDate: string | null = null;
  districtUuid: string | null = null;
  retailerId: number | null = null;

  constructor(obj: any) {
    Object.assign(this, obj);
  }
}

export class OfferingCardReaderConfig {
  cardReaderCode: string | null = null;
  uuid: string | null = null;
  offeringUuid: string | null = null;
  startDate: string | null = null;
  endDate: string | null = null;
  configDomainPrefix: string | null = null;

  constructor(obj: any) {
    Object.assign(this, obj);
  }
}

export class DistrictCardReader {
  cardReaderCode: string | null = null;
  uuid: string | null = null;
  leaseUuid: string | null = null;
  startDate: string | null = null;
  endDate: string | null = null;

  constructor(obj: any) {
    Object.assign(this, obj);
  }
}

export class SpaceSelection {
  leaseUuid: string | null = null;
  retailerId: number | null = null;
  districtUuid: string | null = null;
  occupancyType: OccupancyType | null = null;
  agreementType: AgreementType | null = null;
  startDate: string | null = null;
  endDate: string | null = null;
  selectedFloorSpaceNames: string[] = [];
  editSpaces: boolean = false;
  periodGroupRanking: number | null = null;
  periodGroupUuid: string | null = null;

  // New: pricing period and rates chosen during creation
  rentalTimePeriod?: TimePeriod | null;
  rentalRates?: SpaceDefaultRate[] | null;

  constructor(leaseUuid: string | null,
              retailerId: number | null,
              districtUuid: string | null,
              occupancyType: OccupancyType | null,
              agreementType: AgreementType | null,
              startDate: string | null,
              endDate: string | null,
              selectedFloorSpaceNames: string[],
              editSpaces: boolean,
              periodGroupRanking: number | null,
              periodGroupUuid: string | null) {
    this.leaseUuid = leaseUuid;
    this.retailerId = retailerId;
    this.districtUuid = districtUuid;
    this.occupancyType = occupancyType;
    this.agreementType = agreementType;
    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedFloorSpaceNames = selectedFloorSpaceNames;
    this.editSpaces = editSpaces;
    this.periodGroupRanking = periodGroupRanking;
    this.periodGroupUuid = periodGroupUuid;
  }
}

export interface LeaseDetailsInfoPanel {
  message: string[],
  type: PanelType,
  buttons: boolean,
  buttonLabels: (string | null)[],
  buttonDisabled: (boolean)[],
  display: boolean,
  code: string
}

export interface LeaseApprovalPanel {
  message: string,
  type: string,
  approvalButton: boolean,
  approvalButtonLabel: string | null,
  approvalButtonType: string,
  approvalButtonDisabled: boolean,
  rejectButton: boolean,
  rejectButtonLabel: string | null,
  rejectButtonType: string,
  rejectButtonDisabled: boolean
  display: boolean
}

export interface OccupiedSpace {
  districtSpace: OfferingSpacesInterface,
  leaseGridSpaceGroups: LeaseGridSpaceGroup[]
}

export interface LeaseGridSpaceGroup {
  groupUuid: string,
  startDate: string,
  endDate: string,
  spaceId: number,
  spaceName: string,
  leaseGridSpaces: LeaseGridSpace[]
}


export interface DistrictGridSpacesForTerm {
  leaseSpaces: LeaseSpaceForTerm[];
  maxFloorSpaceForTerm: number;
  startDate: Date;
  endDate: Date;
}

export interface LeaseSpaceForTerm {
  districtSpaceId: number;
  spaceName: string;
  spaceSize: number;
  availabilityStatus: string;
  leasedCompanyName: string;
  leaseUuid: string;
  defaultPricing: SpaceDefaultRate[];
}

export interface SubLeaseRequest {
  retailerId: number | null;
  offeringUuid: string;
  subLeaseUuid: string | null;
  leaseUuid: string;
  spaceUuid: string | null;
  occupiedSpace: number;
  leaseStartDate: string;
  leaseEndDate: string;
}

export interface SharedLeasesSpaceSize {
  uuid: string | null;
  subLeaseUuid: string;
  leaseUuid: string;
  occupiedSpace: number;
  startDate: string;
  endDate: string;
  availabilityStatus: string
}

export enum OccupancyType {
  SINGLE = 'SINGLE',
  SHARED = 'SHARED'
}

export enum AgreementType {
  LEASE = 'LEASE',
  SUB_LEASE = 'SUB_LEASE'
}

export enum TransactionSource {
  VEND = "VEND",
  VEND_SUPPLIER_OFFERING = "VEND_SUPPLIER_OFFERING",
  VEND_SUPPLIER_LEASE = "VEND_SUPPLIER_LEASE",
  POSTER = "POSTER",
  NETCASH = "NETCASH"
}

export enum LeaseFilterType {
  LEASE_STATUS,
  OCCUPANCY_TYPE,
  TERM_STATUS,
  EXPIRES_IN,
  DATE_RANGE,
  COMPACT_VIEW
}

export interface ProductSupplier {
  uuid: string;
  name: string;
  description: string;
  posAccountUuid: string | null;
}

export enum LeaseViewSection {
  HEADER = "HEADER",
  TABS = "TABS",
  DETAILS = "DETAILS",
  RATES_AND_CHARGES = "RATES_AND_CHARGES",
  SPACES = "SPACES",
  SUB_LEASES = "SUB_LEASES",
  DOCUMENTS = "DOCUMENTS",
  POINT_OF_SALE = "POINT_OF_SALE",
  TURNOVER = "TURNOVER",
  SHOWCASE = "SHOWCASE"
}
